'use client';

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cosechaService, ReporteTabularResponse } from '@/lib/api/cosechaService';
import ErrorMessage from '@/components/common/ErrorMessage';

const CosechasManagement: React.FC = () => {
  const [producto, setProducto] = useState('General');
  // Sin filtros de fecha por defecto para mostrar todos los datos disponibles
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [contenidoTipo, setContenidoTipo] = useState('Total Vencido');
  const [accion, setAccion] = useState('Tabular Periodo/Otorgamiento');
  const [reporte, setReporte] = useState<ReporteTabularResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);

  const handleConsultar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReporte(null);

    try {
      const response = await cosechaService.getTabular({
        producto,
        fecha_inicio: fechaInicio || undefined,
        fecha_fin: fechaFin || undefined,
        contenido_tipo: contenidoTipo,
        accion,
      });
      if (response.data) {
        setReporte(response.data);
      } else {
        setError(response.error || 'Error al consultar el reporte');
      }
    } catch (err: any) {
      setError(err.message || 'Error al consultar');
    } finally {
      setLoading(false);
    }
  }, [producto, fechaInicio, fechaFin, contenidoTipo, accion]);

  // Helper para extraer mensaje de error
  const extractErrorMessage = (error: unknown): string => {
    if (!error) return 'Error desconocido';
    if (typeof error === 'string') return error;
    if (Array.isArray(error)) {
      // Pydantic validation errors
      return error.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof error === 'object') {
      const err = error as any;
      return err.msg || err.message || err.detail || JSON.stringify(error);
    }
    return String(error);
  };

  const handleUploadCSV = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('El archivo debe ser un CSV');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadMessage(null);

    try {
      const response = await cosechaService.uploadCSV(file);
      if (response.data) {
        setUploadMessage(response.data.message || 'Archivo procesado correctamente');
        // Limpiar input y recargar datos
        e.target.value = '';
        handleConsultar();
      } else {
        setError(extractErrorMessage(response.error));
      }
    } catch (err: any) {
      setError(extractErrorMessage(err.message || err));
    } finally {
      setUploading(false);
    }
  }, [handleConsultar]);

  const handleExportCSV = useCallback(async () => {
    try {
      await cosechaService.exportCSV({
        producto,
        fecha_inicio: fechaInicio || undefined,
        fecha_fin: fechaFin || undefined,
        contenido_tipo: contenidoTipo,
        accion,
      });
    } catch (err: any) {
      setError(err.message || 'Error al exportar el CSV');
    }
  }, [producto, fechaInicio, fechaFin, contenidoTipo, accion]);

  // Auto-cargar al montar el componente
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      handleConsultar();
    }
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cosechas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Reporte de cartera vencida por período y mes de otorgamiento
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleConsultar}
            disabled={loading}
          >
            {loading ? 'Consultando...' : 'Consultar'}
          </Button>
          {reporte && (
            <Button variant="secondary" onClick={handleExportCSV}>
              Exportar CSV
            </Button>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error} />}
      {uploadMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {uploadMessage}
        </div>
      )}

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Producto</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={producto}
              onChange={(e) => setProducto(e.target.value)}
            >
              <option value="General">General</option>
              <option value="Nómina">Nómina</option>
              <option value="Adelanto">Adelanto</option>
              <option value="CNP">CNP</option>
              <option value="Seguro">Seguro</option>
              <option value="ARF">ARF</option>
              <option value="ARR">ARR</option>
              <option value="CSP">CSP</option>
              <option value="Factoraje">Factoraje</option>
            </select>
          </div>
          <Input
            label="Fecha inicio"
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
          <Input
            label="Fecha fin"
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contenido</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={contenidoTipo}
              onChange={(e) => setContenidoTipo(e.target.value)}
            >
              <option value="Total Vencido">Total Vencido</option>
              <option value="Capital Vencido">Capital Vencido</option>
              <option value="Interes Vencido">Interés Vencido</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vista</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={accion}
              onChange={(e) => {
                setAccion(e.target.value);
                setReporte(null);
              }}
            >
              <option value="Tabular Periodo/Otorgamiento">Tabular Periodo/Otorgamiento</option>
              <option value="Tabular Otorgamiento/Periodo">Tabular Otorgamiento/Periodo</option>
            </select>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">Subir archivo CSV</label>
          <div className="flex gap-2 items-center">
            <input
              type="file"
              accept=".csv"
              onChange={handleUploadCSV}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
            />
            {uploading && <span className="text-sm text-gray-500">Procesando...</span>}
          </div>
        </div>
      </div>

      {reporte && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 overflow-x-auto">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Reporte de Cosechas</h2>
            <p className="text-sm text-gray-500">
              {reporte.accion} - {reporte.contenido_tipo}
            </p>
          </div>

          <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {accion.includes('Periodo/Otorgamiento') ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">
                      PERÍODO
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">
                      OTORGAMIENTO
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">
                      OTORGAMIENTO
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase border border-gray-300">
                      PERÍODO
                    </th>
                  </>
                )}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase border border-gray-300">
                  COLOCACIÓN
                </th>
                {reporte.buckets.map((bucket) => (
                  <th
                    key={bucket}
                    className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase border border-gray-300"
                  >
                    {bucket}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(reporte.renglones).map(([grupo, renglones]) =>
                Object.entries(renglones).map(([renglon, datos], idx) => (
                  <tr key={`${grupo}-${renglon}-${idx}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 border border-gray-300">
                      {grupo}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 border border-gray-300">
                      {renglon}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 border border-gray-300">
                      {formatCurrency(Number(datos.monto_colocacion))}
                    </td>
                    {reporte.buckets.map((bucket) => (
                      <td
                        key={bucket}
                        className="px-4 py-3 text-sm text-right text-gray-700 border border-gray-300"
                      >
                        {formatCurrency(
                          Number(
                            (datos[reporte.contenido_tipo as keyof typeof datos] as Record<string, number>)?.[bucket] || 0
                          )
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {Object.keys(reporte.renglones).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No hay datos para mostrar con los filtros seleccionados
            </div>
          )}
        </div>
      )}

      {!reporte && !loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-sm text-gray-500 text-center">
          Selecciona los filtros y haz clic en &quot;Consultar&quot; para ver el reporte
        </div>
      )}
    </div>
  );
};

export default CosechasManagement;
